import type { DesktopPrinterModelId } from '../printerModelConfig';
import { rasterDotsForWidthMm } from '../receiptLayout';
import { desktopBridgeProvider } from './DesktopBridgeProvider';
import type { DesktopPrinter, DesktopPrinterState } from './types';

/** States where a job can still be sent (paper-out / cover-open queue until fixed on most firmware). */
const PRINTABLE_STATES: ReadonlySet<DesktopPrinterState> = new Set(['ready', 'unknown', 'paper-out', 'cover-open']);

export type DesktopEncodeOptions = {
  modelId: DesktopPrinterModelId;
  lineWidth: number;
  rasterDotsPerLine: number;
};

/** 80 mm → 48 cols / 576 dots; 58 mm → 32 cols / 384 dots (203 dpi, printable width). */
export function resolveDesktopEncodeOptions(printer: Pick<DesktopPrinter, 'config' | 'modelId'>): DesktopEncodeOptions {
  const modelId = printer.config.modelId ?? printer.modelId ?? 'xp-80t';
  if (printer.config.paperWidthMm === 58) {
    return { modelId, lineWidth: 32, rasterDotsPerLine: rasterDotsForWidthMm(48) };
  }
  return { modelId, lineWidth: 48, rasterDotsPerLine: 576 };
}

export function isDesktopPrinterPrintable(printer: DesktopPrinter | null | undefined): boolean {
  return Boolean(printer && PRINTABLE_STATES.has(printer.state));
}

export const desktopBridgePrinterAdapter = {
  getActivePrinter(): DesktopPrinter | null {
    const snap = desktopBridgeProvider.getSnapshot();
    return snap.status === 'ready' ? snap.activePrinter : null;
  },

  /** Bridge reachable and a receipt printer is selected that can take jobs. */
  isConnected(): boolean {
    return isDesktopPrinterPrintable(this.getActivePrinter());
  },

  /** `raw` skips the bridge's cut / drawer / beep post-processing (control bytes). */
  async print(bytes: Uint8Array, options: { requestId?: string; raw?: boolean } = {}): Promise<void> {
    const printer = this.getActivePrinter();
    if (!printer) throw new Error('bluetoothPrinter.desktop.errors.noPrinter');
    await desktopBridgeProvider.print(bytes, { printerId: printer.id, jobId: options.requestId, raw: options.raw });
  },

  /** Waits for the first bridge probe so the very first sale after page load still auto-prints. */
  async ensureReady(): Promise<boolean> {
    desktopBridgeProvider.start();
    if (desktopBridgeProvider.getSnapshot().status === 'idle') await desktopBridgeProvider.probe();
    await desktopBridgeProvider.settled();
    return this.isConnected();
  },
};
